import Icon from '@ant-design/icons';
import styled from '@emotion/styled';
import { Alert, Badge, Button, Input, message, Tabs } from 'antd';
import StatusButton from 'components/StatusButton';
import { useMemo, useState } from 'react';
import { get } from 'lodash-es';
import posthog from '@leyan/lytics';
import { modeTranslate } from 'services/api/overseaTranslate';
import { reply } from 'services/api/review';
import { createConversation } from 'services/api/refund';
import copyText from 'utils/copyText';
import StatusSwitch from 'components/StatusSwitch';
import { buyerNickMask } from 'services/channels/demo';
import useModal from 'hooks/useModal';
import {
  useAutoTranslateConfig,
  useSetAutoTranslateConfig,
} from 'hooks/business/im/chatPreference';
import { useConversationRead } from 'hooks/business/useConversationRead';
import ConversationModal from 'views/Setting/Remind/components/ConversationModal';
import { addFakeConversationOrderSn } from 'hooks/business/conversation';
import { isFakeConversation as isFakeConversationFn, useReviewContext } from '../Context';
import { ReactComponent as AppealSvg } from '../assets/appeal.svg';
import { reviewChannelMap } from 'utils/constants';
import { useAuthorization } from 'components/Authorization';
import { useTransAppProductName } from 'hooks/useTrans';
import { useSetConversation } from 'hooks/business/lruConversation';
import { T, useTranslate } from '@tolgee/react';
import { useMemoizedFn, useRequest, useUnmount } from 'ahooks';
import { isTiKTok } from 'lib/utils/isTikTok';
import { useChannel2Platform } from '../hooks/useChannel2Platform';
import { ReplyButton } from './ReplyButton';
import { useIsTikTokReviewStoreOffline, useRefreshTikTokReviewStores } from 'hooks/useTikTokReview';
import { useRegionLanguageMap } from 'hooks/business/system/useRegionLanguage';
import { RegionLangSelect } from 'components/Langs/RegionLangSelect';

const Container = styled.div`
  flex: 0 0 auto;
  background: var(--fett-bg-panel);
  .${({ theme }) => theme.getAntdPrefixedClassName('tabs-nav')} {
    margin-bottom: 0;
    padding: 0 14px;
  }
  textarea {
    height: 60px;
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
    resize: none;
    &[disabled] {
      background-color: unset !important;
    }
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 40px;
  padding: 0 18px;
  border-bottom: solid 1px var(--fett-border-default);
  > span {
    color: var(--fett-text-tertiary);
  }
  > .${({ theme }) => theme.getAntdPrefixedClassName('switch')} {
    margin-left: 10px;
  }
`;

const Content = styled.div`
  position: relative;
`;

const Count = styled.span<{ over: string }>`
  color: ${({ over }) => (over === 'true' ? '#ff4d4f' : 'currentColor')};
`;

const Mask = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-around;
  align-items: center;
  background: rgba(255, 255, 255, 0.8);
  > div {
    line-height: 28px;
    padding: 0 12px;
    color: var(--fett-text-secondary);
    background: #d7e1fa;
    border-radius: 2px;
  }
`;

export const isReplyDetail = (
  detail: Review.ReviewDetail | Review.ReplyDetail,
): detail is Review.ReplyDetail => {
  return detail.commentType === 2;
};

function ReplyEditor() {
  const { conversation, profile, detail, updateStatus, refreshReview, onConversationCreated } =
    useReviewContext();

  const {
    status,
    orderId,
    channel,
    region,
    handleName,
    buyerName,
    buyerId: externalBuyerId,
    storeId: externalStoreId,
    reviewDetails = [],
  } = detail;

  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();

  const { payload } = useAuthorization<Oversea.Token.Payload>();

  const { isShopee, isLazada, isMercado, isTikTokCommon, isTikTokUS } =
    useChannel2Platform()(channel);

  const { name } = payload || {};

  const isFakeConversation = isFakeConversationFn(conversation);

  const isEmptyLazadaReview = isLazada && !detail.buyerComment;

  const isReplied = reviewDetails.some((detail) => isReplyDetail(detail));

  const isTikTokReviewStoreOffline = useIsTikTokReviewStoreOffline(externalStoreId);
  const isTikTokStoreOffline = isTikTokCommon && isTikTokReviewStoreOffline;
  const { loading: refreshTikTokStoresLoading, runAsync: refreshTikTokStores } =
    useRefreshTikTokReviewStores(isTikTokUS ? 'TIKTOK_US' : 'TIKTOK');

  const hasConversationControlPermission =
    isFakeConversation || conversation.controlStatus || conversation.supervisePower;

  const replyEnable =
    hasConversationControlPermission && !isReplied && !isEmptyLazadaReview && !isTikTokStoreOffline;

  const conversationRead = useConversationRead();

  const setConversation = useSetConversation();

  const autoTranslate = useAutoTranslateConfig();

  const setAutoTranslate = useSetAutoTranslateConfig();

  const [replyContent, setReplyContent] = useState('');

  const regionLanguageMap = useRegionLanguageMap();

  const [buyerLanguage, setBuyerLanguage] = useState(regionLanguageMap[region]);

  const { visible, show, hide, meta } = useModal<string>();

  // 是否支持回复
  const isSupportReply = useMemo(() => !isMercado, [isMercado]);
  // 最大内容长度
  const maxContentLength = useMemo(() => (isTikTokCommon ? 300 : 500), [isTikTokCommon]);

  const { runAsync: startRefreshPolling, cancel: stopRefreshPolling } = useRequest(
    () => refreshReview(),
    {
      manual: true,
      pollingInterval: isTikTokCommon ? 3000 : undefined,
    },
  );

  const alert = useMemo(() => {
    if (isEmptyLazadaReview) {
      return (
        <Alert
          banner
          type="warning"
          message={t('scene.reviewWorkspace.reply.error.noTextContent')}
        />
      );
    }

    if (replyEnable) {
      if (isTiKTok(reviewChannelMap[channel])) {
        return (
          <Alert
            banner
            type="warning"
            message={t('scene.reviewWorkspace.reply.hint.replyOnceBothReviews')}
          />
        );
      }

      return (
        <Alert
          banner
          type="warning"
          message={t('scene.reviewWorkspace.reply.hint.replyOnceWarning')}
        />
      );
    }

    return null;
  }, [replyEnable, isEmptyLazadaReview, channel, t]);

  const extra = useMemo(() => {
    if (replyEnable) {
      return null;
    }

    if (isTikTokCommon) {
      // 卖家回复主评
      const replyList = reviewDetails?.filter(
        (item) => item.commentType === 2,
      ) as Review.ReplyDetail[];
      const enable = replyList.every((r) => r.status === 3);
      if (enable) {
        return null;
      }
    }

    if (status === 3) {
      return (
        <Mask>
          <div>{t('domain.review.status.processed')}</div>
        </Mask>
      );
    }

    if (status === 4) {
      return (
        <Mask>
          <div>{t('domain.review.status.noActionNeeded')}</div>
        </Mask>
      );
    }

    if (isEmptyLazadaReview) {
      return <Mask />;
    }

    // 检测 TikTok 数据状态
    if (isTikTokStoreOffline) {
      return (
        <Mask>
          <span>
            <p className="text-center px-6 mb-1">
              {t('scene.reviewWorkspace.list.error.tiktokDataAbnormal')}
            </p>
            <p className="text-center px-6 mb-0">
              <T
                keyName="TT评价异常提示内容操作"
                params={{
                  com: (
                    <Button
                      type="text"
                      className="!text-primary"
                      loading={refreshTikTokStoresLoading}
                      onClick={refreshTikTokStores}
                    />
                  ),
                }}
              />
            </p>
          </span>
        </Mask>
      );
    }

    if (handleName && handleName !== name) {
      return (
        <Mask>
          <div>{t('scene.reviewWorkspace.detail.status.agentProcessing', { x: handleName })}</div>
        </Mask>
      );
    }

    if (!hasConversationControlPermission && conversation.receptionName) {
      return (
        <Mask>
          <div>
            {t('scene.reviewWorkspace.reply.hint.forwarded', {
              name: conversation.receptionName,
            })}
          </div>
        </Mask>
      );
    }

    return <Mask />;
  }, [
    replyEnable,
    status,
    isEmptyLazadaReview,
    handleName,
    name,
    hasConversationControlPermission,
    conversation.receptionName,
    isTikTokStoreOffline,
    refreshTikTokStoresLoading,
    isTikTokCommon,
    reviewDetails,
    refreshTikTokStores,
    t,
  ]);

  const modal = meta && (
    <ConversationModal
      forceAcquireControlReason="PROCESS_RATING"
      visible={visible}
      onCancel={() => hide()}
      record={{
        conversationId: meta,
        buyerNick: buyerNickMask(buyerName || ''),
      }}
    />
  );

  const openConversation = useMemoizedFn(() => {
    createConversation({
      externalBuyerId,
      buyerNick: buyerName,
      region,
      externalStoreId,
      channel: reviewChannelMap[channel],
      orderId: detail.orderId,
      sourceAction: 'RATING',
    })
      .then((newConversation) => {
        const { conversationId, controlStatus, supervisePower, mockExternal, channel } =
          newConversation;
        setConversation(conversationId, newConversation);
        show(conversationId);
        if (controlStatus && !supervisePower) {
          conversationRead(newConversation);
        }

        if (status === 1 && (controlStatus || supervisePower)) {
          updateStatus(2, true);
        }

        if (mockExternal && channel === 'SHOPEE') {
          addFakeConversationOrderSn(conversationId, orderId);
        }

        onConversationCreated(newConversation);

        return newConversation;
      })
      .catch(() => {
        message.error(t('scene.reviewWorkspace.list.error.fetchConversationFailed'));
      });

    posthog.capture('$review', {
      type: 'manualOperate',
      operate: 'chat',
    });
  });

  // 回复主评
  const replyMainReview = useMemoizedFn(async (commentId?: string) => {
    if (!replyContent.trim()) {
      message.error(t('scene.reviewWorkspace.reply.placeholder.enterReply'));
      return;
    }

    if (replyContent.length > maxContentLength) {
      message.error(
        t('scene.reviewWorkspace.reply.validation.contentExceedsLimit', { n: maxContentLength }),
      );
      return;
    }

    const replyComment = (content: string) => {
      return reply({
        channel,
        commentId: commentId || profile.commentId,
        content,
      })
        .then(() => {
          if (status === 1) {
            return updateStatus(2, true);
          }

          return startRefreshPolling();
        })
        .then(() => {
          setReplyContent('');
        })
        .catch((err) => {
          if (get(err, 'response.status') === 409) {
            message.warn(t('scene.reviewWorkspace.detail.feedback.alreadyProcessed'));
          } else {
            message.error(t('scene.reviewWorkspace.reply.feedback.replyFailed'));
          }
        });
    };

    posthog.capture('$review', {
      type: 'manualOperate',
      operate: 'reply',
    });

    if (autoTranslate) {
      try {
        const res = await modeTranslate(replyContent, buyerLanguage, 'GOOGLE', { from: 'review' });
        if (res.length > maxContentLength) {
          message.error(
            t('scene.reviewWorkspace.reply.validation.translatedContentExceedsLimit', {
              n: maxContentLength,
            }),
          );
          return;
        }
        return replyComment(res);
      } catch (err) {
        message.error(t('scene.reviewWorkspace.reply.error.translationFailed'));
      }
    }

    return replyComment(replyContent);
  });

  useUnmount(() => {
    stopRefreshPolling();
  });

  if (!isSupportReply) {
    return (
      <>
        {modal}
        <div className="w-full h-28 flex items-center justify-center bg-fill-0">
          <Button type="primary" onClick={openConversation}>
            {appProductNameT('review.contactBuyer')}
          </Button>
        </div>
      </>
    );
  }

  return (
    <Container>
      {modal}
      {alert}
      <Tabs
        activeKey="reply"
        tabBarExtraContent={
          isShopee || isTikTokCommon ? null : (
            <Button
              icon={<Icon component={AppealSvg} />}
              onClick={() => {
                copyText(orderId)
                  .then(() => {
                    message.success(t('scene.reviewWorkspace.detail.feedback.orderNumberCopied'));
                  })
                  .catch(() => {
                    message.error(t('common.feedback.copyFailed'));
                  });
              }}
            >
              {t('scene.reviewWorkspace.detail.action.appealToPlatform')}
            </Button>
          )
        }
        onChange={(key) => {
          if (key !== 'chat') {
            return;
          }
          openConversation();
        }}
      >
        <Tabs.TabPane key="reply" tab={t('scene.reviewWorkspace.reply.section.reply')} />
        <Tabs.TabPane
          key="chat"
          tab={
            <Badge
              dot={
                conversation.unreadCount > 0 &&
                conversation.controlStatus &&
                !conversation.supervisePower
              }
            >
              {appProductNameT('review.contactBuyer')}
            </Badge>
          }
        />
      </Tabs>

      <Toolbar>
        <span>{t('scene.reviewWorkspace.reply.field.buyerLanguage')}</span>
        <RegionLangSelect
          bordered={false}
          region={region}
          value={buyerLanguage}
          onChange={setBuyerLanguage}
        />
        <span>{t('scene.reviewWorkspace.reply.action.autoTranslate')}</span>
        <StatusSwitch
          checked={autoTranslate}
          onChange={(checked) => setAutoTranslate(checked)}
          checkedChildren={t('common.switch.on')}
          unCheckedChildren={t('common.switch.off')}
        />
      </Toolbar>
      <Content>
        <Input.TextArea
          value={replyContent}
          onChange={({ target: { value } }) => setReplyContent(value)}
          placeholder={t('scene.reviewWorkspace.reply.placeholder.enterReply')}
        />
        <div className="flex justify-between items-end px-4 py-2">
          <Count over={String(replyContent.length > maxContentLength)}>
            {replyContent.length}/{maxContentLength}
          </Count>
          <div className="flex items-center gap-3">
            <StatusButton
              onClick={() => {
                return modeTranslate(replyContent, buyerLanguage, 'GOOGLE', { from: 'review' })
                  .then((res) => {
                    setReplyContent(res);
                  })
                  .catch(() => {
                    message.error(t('scene.reviewWorkspace.reply.error.translationFailed'));
                  });
              }}
            >
              {t('scene.reviewWorkspace.reply.action.translate')}
            </StatusButton>
            <ReplyButton onSubmit={replyMainReview} />
          </div>
        </div>
        {extra}
      </Content>
    </Container>
  );
}

export default ReplyEditor;
